/* eslint-disable arrow-body-style */
import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useIntl } from 'umi';
import { useGetState, useAsyncEffect } from 'ahooks';
import { Layout, Card, Button, Modal } from 'tea-component';
import Abnormal from '@home/components/Abnormal';
import Filter from './components/Filter';
import Uploader from './components/Uploader';
import VideoCard from './components/VideoCard';
import VideoPlayer from './components/VideoPlayer';
import useVideosPollingEffect from './hooks/useVideosPollingEffect';
import useUploadEffect from './hooks/useUploadEffect';
import usePlayVideoEffect from './hooks/usePlayVideoEffect';
import useDeleteVideoEffect from './hooks/useDeleteVideoEffect';
import { fetchGames, fetchVideos } from './services';
import { buriedPointPage } from '@home/utils/buriedPoint';
import { loadVideoByFile } from '@home/utils/videoUtils';
import { buildVideo, getFileSimpleName, transformVideos, generateMockUUID, isVideoAndCoverProcessing } from './utils';
import { UploadStatus } from './enums';
import { EXAMPLES_VIDEOS } from './constants';

import type { IGame, IFilter, IVideo, IVideosInfos, IFetchVideosParams } from './types';

import './index.scss';

const { Content } = Layout;

const mergeVideosWithVideosInfos = (videos: IVideo[], videosInfos: IVideosInfos) => {
  if (!Array.isArray(videos)) return [];
  return videos.map((video) => {
    const itemVideoInfo = videosInfos?.[video?.uuid] || {};
    if (!isVideoAndCoverProcessing(video?.status)) return video;
    return {
      ...video,
      ...itemVideoInfo,
    };
  });
};

interface IMotionCaptureProps {
  className?: string;
  style?: React.CSSProperties;
}

const MotionCapture: React.FC<IMotionCaptureProps> = (props) => {
  const { className, style } = props;

  const { formatMessage } = useIntl();

  const [isLoading, setIsLoading] = useState(true);
  const [games, setGames] = useState<IGame[]>([]);

  const [filter, setFilter] = useState<IFilter>({
    role_model_type: 'male',
  });

  /**
   * 视频列表，包含上传中的视频、处理中的视频、处理成功的视频、处理失败的视频
   * getVideos方法 （React机制原因,setState后，无法立即访问到最新的state 这种场景）
   */
  const [videos, setVideos, getVideos] = useGetState<IVideo[]>([]);

  const [exampleVideos] = useState<IVideo[]>(EXAMPLES_VIDEOS);

  /**
   * 视频信息轮询，返回视频的进度状态、封面、处理后的视频链接、拼接的视频链接、处理失败的原因
   * 特殊场景下，需要通过getVideosInfos方法访问到最新的videosInfos数据（React机制原因,setState后，无法立即访问到最新的state 这种场景）
   */
  const [videosInfos, getVideosInfos] = useVideosPollingEffect({
    filter,
    videos,
  });

  /**
   * 组合videos、和videosInfos，返回数据格式 跟videos一模一样，只是给每个video对象 追加了最新的 视频信息状态
   * 所以不要直接访问videos这个state，而是应该访问composedVideos这个state
   */
  const composedVideos = useMemo<IVideo[]>(() => {
    return mergeVideosWithVideosInfos(videos, videosInfos);
  }, [videos, videosInfos]);

  // 视频上传相关逻辑
  const { onUpload } = useUploadEffect();

  // 视频播放、视频下载相关逻辑
  const { player, visible, url, poster, onPlayPre, onDownload, onClose } = usePlayVideoEffect();

  // 删除视频信息相关逻辑
  const { onDelete } = useDeleteVideoEffect({
    videos: composedVideos,
    setVideos,
  });

  const updateVideos = async (params: IFetchVideosParams) => {
    try {
      setIsLoading(true);
      const { motion_video_info: motionVideoInfo } = await fetchVideos(params);
      setVideos(transformVideos(motionVideoInfo));
    } catch (exception) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (filterPayload: IFilter) => {
    const filterTmp = {
      ...filter,
      ...filterPayload,
    };
    setFilter(filterTmp);
    setVideos([]);
    updateVideos(filterTmp);
  };

  const getCommingVideos = async (files: File[]): Promise<IVideo[]> => {
    if (!Array.isArray(files)) return [];
    const commingVideos: IVideo[] = [];
    for (let i = 0;i < files?.length;i++) {
      const file = files?.[i];
      const video = await loadVideoByFile(file);
      const videoName = getFileSimpleName(file?.name);
      const duration = video?.duration;
      const mNow = moment();
      const mockUUID = generateMockUUID({
        index: i,
        name: videoName,
        timestamp: mNow.milliseconds(),
      });
      const commingLocalVideo = buildVideo({
        uuid: mockUUID,
        status: UploadStatus.Loading,
        origin_video_name: videoName,
        origin_video_time: Number.isNaN(duration) ? '' : String(Math.floor(duration)),
        create_time: mNow.format('YYYY-MM-DD HH:mm:ss'),
      });
      commingVideos.push(commingLocalVideo);
    }
    return commingVideos;
  };

  const updateVideosAndUpload = async (commingFiles: File[]) => {
    const commingVideos = await getCommingVideos(commingFiles);
    const videosTmp = [...commingVideos, ...composedVideos];
    setVideos(videosTmp);
    onUpload(commingFiles, commingVideos, filter, {
      callback: (uuid: string, options: Partial<IVideo>) => {
        const currentVideos = mergeVideosWithVideosInfos(getVideos(), getVideosInfos());
        const targetVideoIndex = currentVideos?.findIndex((item) => item?.uuid === uuid);
        const targetVideo = currentVideos?.[targetVideoIndex];
        const nextVideos = [
          ...currentVideos?.slice(0, targetVideoIndex),
          { ...targetVideo, ...options },
          ...currentVideos?.slice(targetVideoIndex + 1),
        ];
        setVideos(nextVideos);
      },
    });
  };

  const handleFilesSelect = (commingFiles: File[]) => {
    updateVideosAndUpload(commingFiles);
  };

  const renderVideoListUI = (videos: IVideo[], showDeleteIcon?: boolean) => {
    if (!videos?.length) return null;
    return (
      <div className="dba-motion-capture__video-list">
        {videos?.map((video) => (
          <VideoCard
            key={video?.uuid}
            className="dba-motion-capture__video-list__video-item"
            showDeleteIcon={showDeleteIcon}
            data={video}
            onPlay={onPlayPre}
            onDelete={(video) => {
              if (!showDeleteIcon) return;
              onDelete(video, filter);
            }}
          />
        ))}
      </div>
    );
  };

  useAsyncEffect(async () => {
    const res = await fetchGames(filter);
    const gamesTmp = res || [];
    setGames(gamesTmp);

    updateVideos({
      role_model_type: filter?.role_model_type,
    });
  }, []);

  useEffect(() => {
    buriedPointPage({
      pageId: '700701',
      pageName: 'motion-capture',
      extInfo: '',
      pagePath: '/motion-capture',
      typeId: 'aigc',
    });
  }, []);

  return (
    <>
      <Content className={classnames('dba-motion-capture', className)} style={style}>
        <Content.Header title={formatMessage({ id: 'dba_motion_capture' })} />
        <Content.Body full className={'tools-body'}>
          {/* 筛选器 */}
          <Filter games={games} value={filter} onChange={handleFilterChange} />
          <Card>
            <Card.Body style={{ paddingBottom: composedVideos?.length ? 0 : 20 }}>
              <Abnormal
                isLoading={isLoading}
                isNoData={false}
                isError={false}
                component={
                  <>
                    {/* 上传控件 */}
                    <Uploader style={{ marginBottom: composedVideos?.length ? 20 : 0 }} onSelect={handleFilesSelect} />
                    {/* 最近上传视频、临时上传视频列表 */}
                    {renderVideoListUI(composedVideos, true)}
                  </>
                }
              />
            </Card.Body>
          </Card>
          {/* Some Examples模块 */}
          <Card>
            <Card.Body style={{ paddingBottom: exampleVideos?.length ? 0 : 20 }} title={formatMessage({ id: 'dba_motion_capture_some_examples' })}>
              {/* Some Examples视频列表 */}
              {renderVideoListUI(exampleVideos)}
            </Card.Body>
          </Card>
        </Content.Body>
      </Content>
      {/* 播放弹窗 */}
      <Modal
        className="dba-motion-capture-video-play-modal"
        size="xl"
        caption={formatMessage({ id: 'dba_motion_capture_preview' })}
        visible={visible}
        onClose={onClose}
      >
        <Modal.Body>
          <div className="dba-motion-capture-video-play-modal__content-title">
            <div className="dba-motion-capture-video-play-modal__content-title__content-title-item">
              {formatMessage({ id: 'dba_motion_capture_original_video' })}
            </div>
            <div className="dba-motion-capture-video-play-modal__content-title__content-title-item">
              {formatMessage({ id: 'dba_motion_capture_new_video' })}
            </div>
          </div>
          {/* 视频播放器 */}
          <VideoPlayer ref={player} posters={[poster]} sources={[{ src: url }]} />
          {/* 原生video播放视频 没有声音，暂时还是使用VideoPlayer组件 */}
          {/* <video
            ref={player}
            autoPlay
            controls
            src={url}
            poster={poster}
            style={{ width: 910, height: 510, backgroundColor: 'rgb(0, 0, 0)', objectFit: 'contain' }}
          ></video> */}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            onClick={(e) => {
              e?.stopPropagation();
              e?.preventDefault();
              onDownload('result_video_url');
            }}
          >
            {formatMessage({ id: 'dba_motion_capture_download_new_video' })}
          </Button>
          <Button
            onClick={(e) => {
              e?.stopPropagation();
              e?.preventDefault();
              onDownload('concat_video_url');
            }}
          >
            {formatMessage({ id: 'dba_motion_capture_download_comparison_video' })}
          </Button>
          <Button onClick={onClose}>{formatMessage({ id: 'aigc_common_cancel' })}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MotionCapture;
